'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';

interface OrgInputs {
  totalEngineers: number;
  productComplexity: number;
  domainCount: number;
  managementPhilosophy: 'flat' | 'hierarchical' | 'mixed';
  teamTopology: 'stream-aligned' | 'platform' | 'enabling' | 'complicated-subsystem' | 'mixed';
}

interface TeamRecommendation {
  teamCount: number;
  teamSize: number;
  managersNeeded: number;
  techLeadsNeeded: number;
  spanOfControl: number;
  structure: string;
  topologyBreakdown: { type: string; count: number; description: string }[];
  recommendations: string[];
  orgChart: OrgNode;
}

interface OrgNode {
  name: string;
  role: string;
  reports: OrgNode[];
}

export default function EngineeringOrgDesignPage() {
  const [inputs, setInputs] = useState<OrgInputs>({
    totalEngineers: 20,
    productComplexity: 3,
    domainCount: 2,
    managementPhilosophy: 'mixed',
    teamTopology: 'mixed',
  });

  const updateInput = <K extends keyof OrgInputs>(key: K, value: OrgInputs[K]) => {
    setInputs(prev => ({ ...prev, [key]: value }));
  };

  const recommendation = useMemo((): TeamRecommendation => {
    // Optimal team size: 5-9 engineers (two-pizza team rule)
    const idealTeamSize = 7;
    const teamCount = Math.max(1, Math.ceil(inputs.totalEngineers / idealTeamSize));
    const actualTeamSize = Math.round(inputs.totalEngineers / teamCount);

    // Span of control: flat (8-12), hierarchical (4-6), mixed (6-8)
    const spanOfControlMap = { flat: 10, hierarchical: 5, mixed: 7 };
    const spanOfControl = spanOfControlMap[inputs.managementPhilosophy];

    // Managers needed based on span of control
    const managersNeeded = Math.ceil(teamCount / spanOfControl);

    // Tech leads: 1 per team for technical guidance
    const techLeadsNeeded = teamCount;

    // Determine structure
    let structure = '';
    if (inputs.totalEngineers < 10) {
      structure = 'Single Team: All engineers work together with shared ownership';
    } else if (inputs.totalEngineers < 30) {
      structure = 'Squad Model: Small autonomous teams with embedded leads';
    } else if (inputs.totalEngineers < 100) {
      structure = 'Tribe Model: Multiple squads grouped by domain with tribal leadership';
    } else {
      structure = 'Division Model: Multiple tribes/departments with layered management';
    }

    // Team topology breakdown
    const topologyBreakdown = [];
    if (inputs.teamTopology === 'mixed') {
      const streamAligned = Math.ceil(teamCount * 0.6);
      const platform = Math.ceil(teamCount * 0.2);
      const enabling = Math.ceil(teamCount * 0.1);
      const complicatedSubsystem = teamCount - streamAligned - platform - enabling;

      topologyBreakdown.push(
        { type: 'Stream-Aligned Teams', count: streamAligned, description: 'Feature teams aligned to business capabilities' },
        { type: 'Platform Teams', count: platform, description: 'Internal platform and tooling teams' },
        { type: 'Enabling Teams', count: enabling, description: 'Teams that help others adopt new tech' },
      );
      if (complicatedSubsystem > 0) {
        topologyBreakdown.push({ type: 'Complicated-Subsystem', count: complicatedSubsystem, description: 'Specialized technical systems' });
      }
    } else if (inputs.teamTopology === 'stream-aligned') {
      topologyBreakdown.push({ type: 'Stream-Aligned Teams', count: teamCount, description: 'All teams aligned to business value streams' });
    } else if (inputs.teamTopology === 'platform') {
      topologyBreakdown.push({ type: 'Platform Teams', count: teamCount, description: 'All teams focused on platform capabilities' });
    } else if (inputs.teamTopology === 'enabling') {
      topologyBreakdown.push({ type: 'Enabling Teams', count: teamCount, description: 'All teams focused on enabling others' });
    } else {
      topologyBreakdown.push({ type: 'Complicated-Subsystem Teams', count: teamCount, description: 'All teams on complex technical systems' });
    }

    // Generate recommendations
    const recommendations: string[] = [];

    if (actualTeamSize > 9) {
      recommendations.push(`Team size of ${actualTeamSize} is too large. Consider splitting into ${Math.ceil(actualTeamSize / 7)} smaller teams.`);
    } else if (actualTeamSize < 5) {
      recommendations.push(`Team size of ${actualTeamSize} is small. Consider consolidating teams or hiring more engineers.`);
    } else {
      recommendations.push(`Team size of ${actualTeamSize} is optimal for collaboration and autonomy.`);
    }

    if (inputs.domainCount > teamCount) {
      recommendations.push(`You have ${inputs.domainCount} domains but only ${teamCount} teams. Consider domain consolidation or hiring.`);
    }

    if (spanOfControl > 10) {
      recommendations.push(`Span of control (${spanOfControl}) is high. Managers may be stretched thin - consider adding management layers.`);
    }

    if (inputs.productComplexity >= 4 && inputs.teamTopology !== 'mixed') {
      recommendations.push('High complexity products benefit from mixed team topologies (stream-aligned + platform + enabling).');
    }

    if (managersNeeded === 0 && inputs.totalEngineers > 15) {
      recommendations.push('No dedicated managers for 15+ engineers is risky. Consider adding engineering management.');
    }

    recommendations.push(`Conway's Law: Your architecture will mirror your org structure. Align teams to desired system boundaries.`);

    // Generate org chart
    const generateOrgChart = (total: number, teams: number, teamSz: number, mgrs: number): OrgNode => {
      if (total < 10) {
        // Flat structure
        return {
          name: 'CTO',
          role: 'Chief Technology Officer',
          reports: Array.from({ length: total }, (_, i) => ({
            name: `Engineer ${i + 1}`,
            role: 'Software Engineer',
            reports: [],
          })),
        };
      } else if (total < 30) {
        // Single layer with EMs
        const teamNodes: OrgNode[] = [];
        for (let i = 0; i < teams; i++) {
          teamNodes.push({
            name: `Engineering Manager ${i + 1}`,
            role: 'Engineering Manager',
            reports: Array.from({ length: teamSz }, (_, j) => ({
              name: `Engineer ${i * teamSz + j + 1}`,
              role: 'Software Engineer',
              reports: [],
            })),
          });
        }
        return {
          name: 'CTO',
          role: 'Chief Technology Officer',
          reports: teamNodes,
        };
      } else if (total < 100) {
        // Multiple layers: CTO -> Directors -> Managers -> Engineers
        const directors: OrgNode[] = [];
        const directorsNeeded = Math.ceil(mgrs / 5);
        const managersPerDirector = Math.ceil(mgrs / directorsNeeded);

        for (let d = 0; d < directorsNeeded; d++) {
          const managerNodes: OrgNode[] = [];
          const actualManagersInDir = Math.min(managersPerDirector, mgrs - d * managersPerDirector);

          for (let m = 0; m < actualManagersInDir; m++) {
            managerNodes.push({
              name: `Engineering Manager ${d * managersPerDirector + m + 1}`,
              role: 'Engineering Manager',
              reports: Array.from({ length: teamSz }, (_, e) => ({
                name: `Engineer ${(d * managersPerDirector + m) * teamSz + e + 1}`,
                role: 'Software Engineer',
                reports: [],
              })),
            });
          }

          directors.push({
            name: `Engineering Director ${d + 1}`,
            role: 'Director of Engineering',
            reports: managerNodes,
          });
        }

        return {
          name: 'CTO',
          role: 'Chief Technology Officer',
          reports: directors,
        };
      } else {
        // Large org: CTO -> VP -> Directors -> Managers -> Engineers
        return {
          name: 'CTO',
          role: 'Chief Technology Officer',
          reports: [
            {
              name: 'VP Engineering',
              role: 'Vice President of Engineering',
              reports: Array.from({ length: Math.ceil(mgrs / 10) }, (_, d) => ({
                name: `Director ${d + 1}`,
                role: 'Director of Engineering',
                reports: Array.from({ length: Math.min(5, mgrs - d * 5) }, (_, m) => ({
                  name: `EM ${d * 5 + m + 1}`,
                  role: 'Engineering Manager',
                  reports: Array.from({ length: teamSz }, (_, e) => ({
                    name: `Engineer ${(d * 5 + m) * teamSz + e + 1}`,
                    role: 'Software Engineer',
                    reports: [],
                  })),
                })),
              })),
            },
          ],
        };
      }
    };

    const orgChart = generateOrgChart(inputs.totalEngineers, teamCount, actualTeamSize, managersNeeded);

    return {
      teamCount,
      teamSize: actualTeamSize,
      managersNeeded,
      techLeadsNeeded,
      spanOfControl,
      structure,
      topologyBreakdown,
      recommendations,
      orgChart,
    };
  }, [inputs]);

  // Recursive component to render org chart
  const OrgChartNode = ({ node, depth = 0 }: { node: OrgNode; depth?: number }) => {
    const isLeaf = node.reports.length === 0;
    const levelColors = ['bg-accent-muted border-accent', 'bg-blue-500/20 border-blue-400', 'bg-green-500/20 border-green-400', 'bg-purple-500/20 border-purple-400'];
    const colorClass = levelColors[Math.min(depth, levelColors.length - 1)];

    return (
      <div className="flex flex-col items-center">
        <div className={`px-4 py-2 rounded border-2 ${colorClass} text-center min-w-[140px] mb-2`}>
          <div className="font-bold text-foreground text-sm">{node.name}</div>
          <div className="text-xs text-foreground-secondary">{node.role}</div>
        </div>
        {node.reports.length > 0 && (
          <div className="flex flex-col items-center">
            <div className="w-0.5 h-4 bg-cream/30"></div>
            <div className={`flex ${node.reports.length > 6 ? 'flex-wrap justify-center max-w-4xl' : 'gap-4'}`}>
              {node.reports.map((child, idx) => (
                <div key={idx} className="relative">
                  {!isLeaf && idx > 0 && <div className="absolute top-0 left-0 w-4 h-0.5 bg-cream/30 -translate-x-full"></div>}
                  <OrgChartNode node={child} depth={depth + 1} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-4xl">🏢</span>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">Engineering Org Design Planner</h1>
          </div>
          <p className="text-xl text-foreground-secondary">Design your engineering organization structure using Team Topologies principles</p>
        </div>

        <nav className="mb-8 text-sm text-foreground-tertiary">
          <Link href="/" className="hover:text-accent transition-colors">Home</Link>
          <span className="mx-2">/</span>
          <Link href="/tools" className="hover:text-accent transition-colors">Tools</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">Engineering Org Design</span>
        </nav>

        <div className="bg-accent-muted border border-border rounded-lg p-6 mb-8">
          <h3 className="font-bold text-accent mb-2">How to use this tool</h3>
          <ol className="text-foreground-secondary text-sm space-y-2 list-decimal list-inside">
            <li>Enter your current engineering headcount and product characteristics</li>
            <li>Select your preferred management philosophy and team topology</li>
            <li>Review recommended team structure and organizational design</li>
            <li>Use recommendations to refine your org design and align with Conway&apos;s Law</li>
          </ol>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Organization Inputs</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Total Engineers</label>
                  <input type="number" inputMode="numeric" min="1" max="500" value={inputs.totalEngineers}
                    onChange={(e) => updateInput('totalEngineers', parseInt(e.target.value) || 1)}
                    className="w-full px-4 py-2 bg-background border border-border rounded text-foreground" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Product Complexity: {inputs.productComplexity}/5</label>
                  <input type="range" min="1" max="5" value={inputs.productComplexity}
                    onChange={(e) => updateInput('productComplexity', parseInt(e.target.value))}
                    className="w-full" />
                  <div className="text-xs text-foreground-tertiary mt-1">
                    1 = Simple, 3 = Moderate, 5 = Highly Complex
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">Number of Product Domains</label>
                  <input type="number" inputMode="numeric" min="1" max="20" value={inputs.domainCount}
                    onChange={(e) => updateInput('domainCount', parseInt(e.target.value) || 1)}
                    className="w-full px-4 py-2 bg-background border border-border rounded text-foreground" />
                  <div className="text-xs text-foreground-tertiary mt-1">
                    Distinct business capabilities or product areas
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Management Philosophy</h3>
              <div className="space-y-3">
                {[
                  { value: 'flat', label: 'Flat', desc: 'Wide spans, minimal hierarchy (8-12 reports)' },
                  { value: 'hierarchical', label: 'Hierarchical', desc: 'Narrow spans, more layers (4-6 reports)' },
                  { value: 'mixed', label: 'Mixed', desc: 'Balanced approach (6-8 reports)' },
                ].map(option => (
                  <button key={option.value} onClick={() => updateInput('managementPhilosophy', option.value as OrgInputs['managementPhilosophy'])}
                    className={`w-full p-4 rounded border text-left transition-colors ${
                      inputs.managementPhilosophy === option.value
                        ? 'bg-accent-muted border-accent text-foreground'
                        : 'bg-background border-border text-foreground-secondary hover:bg-accent-muted'
                    }`}>
                    <div className="font-semibold">{option.label}</div>
                    <div className="text-xs text-foreground-tertiary mt-1">{option.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Team Topology</h3>
              <div className="space-y-3">
                {[
                  { value: 'stream-aligned', label: 'Stream-Aligned', desc: 'Feature teams aligned to business value streams' },
                  { value: 'platform', label: 'Platform', desc: 'Internal platform and tooling teams' },
                  { value: 'enabling', label: 'Enabling', desc: 'Teams that help others adopt new practices' },
                  { value: 'complicated-subsystem', label: 'Complicated-Subsystem', desc: 'Teams on specialized technical systems' },
                  { value: 'mixed', label: 'Mixed', desc: 'Combination of multiple topologies (recommended)' },
                ].map(option => (
                  <button key={option.value} onClick={() => updateInput('teamTopology', option.value as OrgInputs['teamTopology'])}
                    className={`w-full p-4 rounded border text-left transition-colors ${
                      inputs.teamTopology === option.value
                        ? 'bg-accent-muted border-accent text-foreground'
                        : 'bg-background border-border text-foreground-secondary hover:bg-accent-muted'
                    }`}>
                    <div className="font-semibold">{option.label}</div>
                    <div className="text-xs text-foreground-tertiary mt-1">{option.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Organization Structure</h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-background rounded p-4 text-center">
                  <div className="text-sm text-foreground-tertiary mb-1">Teams</div>
                  <div className="text-3xl font-bold text-accent">{recommendation.teamCount}</div>
                </div>
                <div className="bg-background rounded p-4 text-center">
                  <div className="text-sm text-foreground-tertiary mb-1">Team Size</div>
                  <div className="text-3xl font-bold text-accent">{recommendation.teamSize}</div>
                </div>
                <div className="bg-background rounded p-4 text-center">
                  <div className="text-sm text-foreground-tertiary mb-1">Managers</div>
                  <div className="text-2xl font-bold text-blue-400">{recommendation.managersNeeded}</div>
                </div>
                <div className="bg-background rounded p-4 text-center">
                  <div className="text-sm text-foreground-tertiary mb-1">Tech Leads</div>
                  <div className="text-2xl font-bold text-green-400">{recommendation.techLeadsNeeded}</div>
                </div>
              </div>
              <div className="p-4 bg-background rounded">
                <div className="text-sm text-foreground-tertiary mb-1">Recommended Structure</div>
                <div className="text-foreground font-semibold">{recommendation.structure}</div>
              </div>
            </div>

            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">Team Topology Breakdown</h3>
              <div className="space-y-3">
                {recommendation.topologyBreakdown.map((topo, idx) => (
                  <div key={idx} className="p-4 bg-background rounded border border-border-muted">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-bold text-foreground">{topo.type}</span>
                      <span className="text-accent font-bold">{topo.count} {topo.count === 1 ? 'team' : 'teams'}</span>
                    </div>
                    <div className="text-xs text-foreground-secondary">{topo.description}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-accent-muted border border-border rounded-lg p-6">
              <h3 className="text-lg font-bold text-accent mb-3">Recommendations</h3>
              <ul className="space-y-2 text-sm text-foreground-secondary">
                {recommendation.recommendations.map((rec, idx) => (
                  <li key={idx}>• {rec}</li>
                ))}
              </ul>
            </div>

            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-lg font-bold text-foreground mb-3">Key Metrics</h3>
              <div className="space-y-2 text-sm text-foreground-secondary">
                <div className="flex justify-between">
                  <span>Span of Control:</span>
                  <span className="text-accent font-bold">{recommendation.spanOfControl} teams/manager</span>
                </div>
                <div className="flex justify-between">
                  <span>Manager Ratio:</span>
                  <span className="text-accent font-bold">{((recommendation.managersNeeded / inputs.totalEngineers) * 100).toFixed(1)}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Teams per Domain:</span>
                  <span className="text-accent font-bold">{(recommendation.teamCount / inputs.domainCount).toFixed(1)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Org Chart Visualization */}
        <div className="mt-8 bg-surface rounded-lg border border-border p-6">
          <h3 className="text-2xl font-bold text-foreground mb-6">Proposed Organization Chart</h3>
          <div className="overflow-x-auto pb-4">
            <div className="min-w-max flex justify-center p-8">
              <OrgChartNode node={recommendation.orgChart} />
            </div>
          </div>
          <div className="mt-6 p-4 bg-accent-muted rounded border border-border">
            <h4 className="text-sm font-bold text-accent mb-2">Legend</h4>
            <div className="flex flex-wrap gap-4 text-xs text-foreground-secondary">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-accent-muted border-2 border-accent rounded"></div>
                <span>Leadership</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-500/20 border-2 border-blue-400 rounded"></div>
                <span>Management</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500/20 border-2 border-green-400 rounded"></div>
                <span>Team Leads</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-purple-500/20 border-2 border-purple-400 rounded"></div>
                <span>Engineers</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
